import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

/**
 * Wrapper around the whole React app. On boot, pings /install/status.php
 * (plain PHP, always responsive — never blocks on Laravel/DB state) and
 * either:
 *   - renders the SPA normally (site is installed), or
 *   - redirects the browser to /install/ (Apache-served pure-PHP wizard).
 *
 * Cached in sessionStorage: only the 'installed' verdict is cached, so a
 * server-side lock-file change propagates on the next tab session.
 */
// When the SPA is built/served pointing at a remote API (REACT_APP_API_BASE_URL
// set — see src/config/api.js), /install/status.php is a same-origin PHP file
// that doesn't exist on this host and the remote one sends no CORS headers for
// it. There's also no meaningful "is this remote backend installed" question to
// ask — it's a live site. Skip the gate entirely in that case.
const REMOTE_API = Boolean(process.env.REACT_APP_API_BASE_URL);

export default function InstallGate({ children }) {
  const location = useLocation();

  const [status, setStatus] = useState(() => {
    if (REMOTE_API) return 'installed';
    try {
      return sessionStorage.getItem('install_status') === 'installed' ? 'installed' : null;
    } catch { return null; }
  });

  useEffect(() => {
    if (status) return; // cached 'installed', or REMOTE_API skip above
    let cancelled = false;

    // Plain fetch — intentionally bypasses axiosInstance so we never touch
    // any Sanctum / baseURL / interceptor config that might be broken on a
    // fresh install. This endpoint just reads a file on disk.
    fetch('/install/status.php', { credentials: 'omit' })
      .then(res => res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`)))
      .then(data => {
        if (cancelled) return;
        if (data.installed) {
          try { sessionStorage.setItem('install_status', 'installed'); } catch {}
          setStatus('installed');
        } else {
          // Hard-redirect to the Apache-served installer. Using window.location
          // instead of <Navigate> because /install/ is a real server directory
          // serving index.php, not a React route.
          try { sessionStorage.removeItem('install_status'); } catch {}
          window.location.replace('/install/');
        }
      })
      .catch(() => {
        if (cancelled) return;
        // /install/status.php is pure PHP — if it's unreachable, the site
        // itself is probably not deployed correctly. Send the visitor to
        // /install/ anyway; its index.php will either render the wizard or
        // the "already installed" page, whichever applies.
        window.location.replace('/install/');
      });

    return () => { cancelled = true; };
  }, [status]);

  if (!status) {
    // First paint before the status call lands. Show nothing to avoid a
    // flash of landing page before the potential redirect.
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-neutral-950">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // status === 'installed' → render the SPA
  return children;
}
